import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import Slider from "components/DesignSystem/Slider/Slider";
import { Form } from "components/icons/Form";
import { HelpCall } from "components/icons/HelpCall";
import { OpenEmail } from "components/icons/OpenEmail";
import { RightArrow } from "components/icons/RightArrow";
import { WhatsApp } from "components/icons/WhatsApp";
import { SALES_EMAIL, INKLE_WEBSITE_URL } from "constants/base";
import { FAILED_COPIED_TEXT } from "constants/documents";
import { BOOK_A_DEMO, EMAIL, PHONE, WHATSAPP } from "constants/help";
import { ANAND_PHONE } from "constants/meetingRequestUrls";
import { useToast } from "hooks/useToast";
import { useHistory } from "react-router-dom";
import "static/styles/components/help.css";
import { checkExternalRoute } from "utils/checkExternalRoute";
import { openLink } from "utils/openLink";

const CopyText = ({
  content,
  onCopy,
}: {
  content: string;
  onCopy: (text: string) => void;
}) => {
  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onCopy(content);
  };

  return (
    <ToolTip text="Click to copy">
      <button
        className="all:unset hover:t-underline t-cursor-pointer"
        onClick={handleCopy}
      >
        {content}
      </button>
    </ToolTip>
  );
};

export const Help = ({ close }: { close: () => void }) => {
  const history = useHistory();
  const { successToast, alertToast } = useToast();

  const handleClick = (actionValue: string) => {
    if (checkExternalRoute(actionValue)) {
      openLink(actionValue);
    } else {
      history.push(actionValue);
    }
    close();
  };

  const copyToClipBoard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      successToast({ message: "Copied successfully!" });
    } catch (err) {
      alertToast({ message: FAILED_COPIED_TEXT });
    }
  };

  const topics = [
    {
      icon: <Form className="t-text-text-30" />,
      title: BOOK_A_DEMO,
      content: (
        <div>
          Book a meeting with a specialist and understand the benefits that
          Inkle offers.
        </div>
      ),
      alt: "FormIcon",
      actionValue: `${INKLE_WEBSITE_URL}demo`,
    },
    {
      icon: <OpenEmail className="t-text-text-30" />,
      title: EMAIL,
      content: (
        <div>
          Write to us at{" "}
          <CopyText content={SALES_EMAIL} onCopy={copyToClipBoard} />
        </div>
      ),
      alt: "MailIcon",
      actionValue: `mailto:${SALES_EMAIL}`,
    },
    {
      icon: <HelpCall className="t-text-text-30" />,
      title: "Call us",
      content: (
        <div>
          Reach us directly at{" "}
          <CopyText content={ANAND_PHONE} onCopy={copyToClipBoard} />
        </div>
      ),
      alt: "Call us",
      actionValue: `tel:${ANAND_PHONE}`,
    },
    {
      icon: <WhatsApp className="t-text-text-30 t-size-5" />,
      title: WHATSAPP,
      content: (
        <div>
          Message us on WhatsApp at{" "}
          <CopyText content={ANAND_PHONE} onCopy={copyToClipBoard} />
        </div>
      ),
      alt: "Whatsapp Icon ",
      actionValue: `https://wa.me/${PHONE}`,
    },
  ];

  return (
    <div>
      <Slider.Header>
        <Slider.Title>Contact us</Slider.Title>
        <Slider.Close />
      </Slider.Header>
      <Slider.Body className="t-space-y-4">
        {topics?.map(({ icon, title, content, alt, actionValue }) => (
          <button
            className="all:unset t-cursor-pointer t-p-4 t-rounded-md t-border t-border-solid t-border-neutral-10 t-w-full t-flex t-items-center t-justify-between t-gap-3"
            key={title}
            onClick={() => handleClick(actionValue)}
          >
            <div className="t-flex t-items-center t-gap-2.5">
              <div className="t-h-11 t-aspect-square t-rounded-full t-bg-surface-lighter-grey t-flex t-items-center t-justify-center">
                {icon}
              </div>
              <div>
                <div className="t-text-subtext-sm t-text-text-100">{title}</div>
                <div className="t-text-body-sm t-text-text-30">{content}</div>
              </div>
            </div>
            <Button customType="ghost_icon">
              <RightArrow />
            </Button>
          </button>
        ))}
      </Slider.Body>
    </div>
  );
};
